import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Shield, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      // Always land on the Dashboard after login, regardless of where the user
      // came from. This keeps behaviour consistent across all roles.
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left: hero */}
      <div className="hidden md:flex md:w-1/2 relative bg-[#0D0D0D] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/19233057/pexels-photo-19233057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0D0D0D] via-[#0D0D0D]/80 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C41E1E] flex items-center justify-center rounded-sm">
              <span className="font-black text-lg">B</span>
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight">BLACKCAT</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">Training & Competency</div>
            </div>
          </div>
          <div>
            <div className="bc-label text-[#C9A84C] mb-3">IATF 16949 · CLAUSE 7.2</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
              The competency<br /> system for<br /> <span className="text-[#C9A84C]">precision</span> manufacturing.
            </h1>
            <p className="mt-6 text-sm text-neutral-300 max-w-md">
              Define, assess, train, and confirm — one auditable trail from skill gap to closure.
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            v2.0 · Blackcat Automotive Electronics
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center bg-[#F9F9F9] px-4">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#C41E1E] flex items-center justify-center rounded-sm">
              <span className="text-white font-black">B</span>
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight">BLACKCAT TMS</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">IATF 16949 · 7.2</div>
            </div>
          </div>

          <div className="bc-card p-8">
            <div className="bc-label mb-2">Authentication</div>
            <h2 className="font-heading text-2xl font-bold mb-1">Sign in to TMS</h2>
            <p className="text-sm text-[#666666] mb-6">Enter your work credentials to continue.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="bc-label block mb-2" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-white border border-[#E5E5E5] rounded-sm px-3 py-2.5 text-sm focus:border-[#C41E1E]"
                  placeholder="name@blackcat.com"
                />
              </div>
              <div>
                <label className="bc-label block mb-2" htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-white border border-[#E5E5E5] rounded-sm px-3 py-2.5 text-sm focus:border-[#C41E1E]"
                  placeholder="Enter password"
                />
              </div>

              {err && (
                <div data-testid="login-error" className="flex items-start gap-2 text-xs text-[#C41E1E] bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" /> {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit-button"
                className="w-full bg-[#C41E1E] hover:bg-[#A31818] disabled:opacity-60 text-white font-medium text-sm rounded-sm py-3 transition-colors flex items-center justify-center gap-2"
              >
                <Shield size={15} /> {busy ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
